import type { GoalProgress } from '@map-my-money/engine';
import { durationText, GOALS_HORIZON_YEARS } from '@map-my-money/views';
import { t } from '../i18n/index.ts';
import { todayDay } from './clock.ts';
import { money, monthYear, toNumber } from './format.ts';
import { goalWhen } from './model.ts';

/**
 * A goal's date as every surface words it (R26), as views `goalStatusText` does for Claude: reached
 * this month, on course for its month, or not within the one horizon; with a "by" day, on track
 * (and how long before the person's date) or how long after it, never "behind" or "late". The
 * shortfall is its own line: `goalShortfall`.
 */
export function goalStatus(g: GoalProgress): string {
  const when = goalWhen(g, todayDay());
  if (when.kind === 'reached') return t('goals.achieved');
  const date = when.kind === 'beyond' ? t('goals.beyond', { years: GOALS_HORIZON_YEARS }) : monthYear(when.date);
  const by = g.deadline;
  if (!by) {
    if (when.kind === 'beyond') return date;
    return when.monthsAway === 0 ? t('goals.onCourse', { date }) : t('goals.eta', { eta: durationText(when.monthsAway), date });
  }
  if (by.onTrack) return by.monthsAhead ? t('goals.by.onTrackAhead', { date, span: durationText(by.monthsAhead) }) : t('goals.by.onTrack', { date });
  return by.monthsBehind ? t('goals.by.after', { span: durationText(by.monthsBehind), by: monthYear(by.date), date }) : date;
}

/** "£370,094 short by Jan 2028" for a goal whose date is after its "by" day; null otherwise. */
export function goalShortfall(g: GoalProgress): string | null {
  const by = g.deadline;
  if (!by || g.isAchieved || by.onTrack || !(toNumber(by.shortfall) > 0)) return null;
  return t('goals.by.short', { amount: money(by.shortfall), by: monthYear(by.date) });
}

/** Mint when a goal with a "by" day is on track, neutral when its date is after that day (no alarm colour); no tone without one. */
export function goalTone(g: GoalProgress): 'text-mint' | 'muted' | undefined {
  if (!g.deadline || g.isAchieved) return undefined;
  return g.deadline.onTrack ? 'text-mint' : 'muted';
}
